import React from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import {
    LayoutDashboard,
    Coffee,
    Users,
    ClipboardList,
    BarChart3,
    LogOut,
    ExternalLink,
    Gift,
    Settings,
} from "lucide-react";
import { clearAdminPin } from "../../lib/admin";
import { logout as googleLogout } from "../../lib/auth";
import TrialBanner from "../../components/TrialBanner";

const AdminLayout = () => {
    const nav = useNavigate();
    const { slug } = useParams();

    const navItems = [
        { to: `/${slug}/admin/dashboard`, icon: LayoutDashboard, label: "Overview", end: true },
        { to: `/${slug}/admin/dashboard/menu`, icon: Coffee, label: "Menu" },
        { to: `/${slug}/admin/dashboard/catalog`, icon: Gift, label: "Catalog" },
        { to: `/${slug}/admin/dashboard/orders`, icon: ClipboardList, label: "Orders" },
        { to: `/${slug}/admin/dashboard/customers`, icon: Users, label: "Customers" },
        { to: `/${slug}/admin/dashboard/reports`, icon: BarChart3, label: "Reports" },
        { to: `/${slug}/admin/dashboard/settings`, icon: Settings, label: "Settings" },
    ];

    const logout = async () => {
        clearAdminPin(slug);
        await googleLogout();
        nav(`/${slug}/admin`, { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <div className="bg-white border-b border-[#E8DCCB] sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="font-display font-black text-2xl">
                            Coffee<span className="text-[#FF4F18]">in7.</span>
                        </div>
                        <div className="text-[#8C7A72]">·</div>
                        <div className="font-display font-bold text-sm text-[#8C7A72]">
                            /{slug} · Owner console
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            data-testid="view-live-btn"
                            href={`/${slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-[#E8DCCB] text-xs font-display font-bold active:scale-95"
                        >
                            <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
                            View customer site
                        </a>
                        <a
                            data-testid="staff-board-link"
                            href={`/${slug}/staff`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#2C1A12] text-white text-xs font-display font-bold active:scale-95"
                        >
                            Staff board
                        </a>
                        <button
                            data-testid="admin-logout"
                            onClick={logout}
                            title="Sign out"
                            className="h-9 w-9 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center active:scale-95"
                        >
                            <LogOut className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr]">
                <aside className="md:sticky md:top-24 self-start min-w-0">
                    <nav className="flex md:flex-col gap-1 overflow-x-auto hide-scroll">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-2xl font-display font-bold text-sm whitespace-nowrap transition-all ${
                                        isActive
                                            ? "bg-[#2C1A12] text-white"
                                            : "text-[#5b4438] hover:bg-white"
                                    }`
                                }
                            >
                                <item.icon className="h-4 w-4" strokeWidth={2.5} />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </aside>

                <main className="min-w-0">
                    <TrialBanner />
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
